/**
 * legacyCopy entries for the SENT_ITEM family — ported verbatim from
 * `SuggestedActionForSentItem` (howToFixIt) and `getSentItemErrorDetailsText`
 * (whatCausedIt) in `../../ParticipantDialog.tsx`.
 *
 * Also includes ASSIGNEE_BOUNCE (a separate family, ported from the same
 * file's `SuggestedActionForAssigneeBounce` / inline `ASSIGNEE_BOUNCE`
 * branch of `getIssueDetailsText`) — placed here rather than in misc.tsx
 * since it may be a duplicate of SENT_ITEM__POSTMARK_BOUNCED; see the
 * comment on that entry below.
 */
import { ChannelType, SupportingActorType } from '@dadohr/shared/domainTypes/experience';
import { UnsafeTFunction } from '../../../../../../i18next';
import { capitalize } from 'lodash';
import React, { ReactNode } from 'react';
import { channelMarkerToString } from '../../../../../../services/channels';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { CopyPack, Issue, IssueKind } from '../types';

const dataOf = (group: Issue[]) => (group[0].raw as { family: 'SENT_ITEM'; data: any }).data;
const ctxOf = (group: Issue[]) => group[0].ctx;
const md = (text: string | null): ReactNode => (text ? <LightMdToJsx>{text}</LightMdToJsx> : null);

const actorLabelOf = (group: Issue[]): string => {
  const c = ctxOf(group);
  const d = dataOf(group);
  const supportingActor = c.supportingActors?.[d.actor];
  return supportingActor?.name || capitalize(d.actor);
};

export const sentItemLegacyCopy: Partial<CopyPack> = {
  [IssueKind.SENT_ITEM__POSTMARK_BOUNCED]: {
    category: 'Message delivery: Email',
    summary: (g) =>
      ctxOf(g).t('Email sent to **{{email}}** bounced', {
        email: dataOf(g).message.recipientEmail
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t('Email sent to **{{email}}** bounced', {
        email: dataOf(g).message.recipientEmail
      }),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Create email or contact Support to update the email address in Dado'))
  },
  // Shares copy with SENT_ITEM__POSTMARK_BOUNCED/INACTIVE_RECIPIENT above by
  // decision — a bounced email reads the same to the reader regardless of
  // which code path recorded it (ExperienceProblemData here vs
  // ConcreteExperienceMessage there). Whether these are actually the same
  // underlying bounce event is a separate, unconfirmed question — out of
  // scope to investigate here.
  [IssueKind.ASSIGNEE_BOUNCE]: {
    category: 'Assignee bounce',
    summary: (g) => {
      const c = ctxOf(g);
      const d = (g[0].raw as { family: 'ASSIGNEE_BOUNCE'; data: any }).data;
      if (d.displayName && d.displayName !== d.email)
        return c.t(`Email sent to **{{name}}** ({{email}}) bounced`, {
          name: d.displayName,
          email: d.email
        });
      if (d.email) return c.t(`Email sent to **{{email}}** bounced`, { email: d.email });
      return c.t(`Email bounced (no recipient address recorded)`);
    },
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.ASSIGNEE_BOUNCE]!.summary(g),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t(
          "Create email or contact Support to update the participant's email address in Dado"
        )
      )
  },
  [IssueKind.SENT_ITEM__POSTMARK_INACTIVE_RECIPIENT]: {
    category: 'Message delivery: Email',
    // Same "bounced" text as SENT_ITEM__POSTMARK_BOUNCED — the original
    // treats InactiveRecipient as another bounce variant.
    summary: (g) =>
      ctxOf(g).t('Email sent to **{{email}}** bounced', {
        email: dataOf(g).message.recipientEmail
      }),
    whatCausedIt: (g) =>
      ctxOf(g).t('Email sent to **{{email}}** bounced', {
        email: dataOf(g).message.recipientEmail
      }),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Create email or contact Support to update the email address in Dado'))
  },
  [IssueKind.SENT_ITEM__POSTMARK_NO_EMAIL]: {
    category: 'Message delivery: Email',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return c.t(`Couldn't send via email - **{{ actor }}** missing **{{ addressType }}**`, {
        actor: actorLabelOf(g),
        addressType: d.message.emailFrom
          ? `${channelMarkerToString(d.message.emailFrom, c.t as UnsafeTFunction)}`
          : c.t('Email')
      });
    },
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__POSTMARK_NO_EMAIL]!.summary(g),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return md(
        c.t('Add **{{ addressType }}** to import source and run a new import', {
          addressType: d.message.emailFrom
            ? channelMarkerToString(d.message.emailFrom, c.t as UnsafeTFunction)
            : c.t('email')
        })
      );
    }
  },
  [IssueKind.SENT_ITEM__POSTMARK_DEFAULT]: {
    category: 'Message delivery: Email',
    summary: (g) => ctxOf(g).t(`Failed to send an email`),
    whatCausedIt: (g) => ctxOf(g).t(`Failed to send an email`),
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  },

  [IssueKind.SENT_ITEM__SLACK_USERS_NOT_FOUND]: {
    category: 'Message delivery: Slack',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      if (!d.message.recipientEmail)
        return c.t(`Couldn't send via Slack - **{{ actor }}** missing **Work Email**`, {
          actor: actorLabelOf(g)
        });
      return c.t(`Couldn't find Slack user with the email **{{ recipientEmail }}**`, {
        recipientEmail: d.message.recipientEmail
      });
    },
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__SLACK_USERS_NOT_FOUND]!.summary(g),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      if (!d.message.recipientEmail)
        return md(c.t('Add **Work Email** to import source and run a new import'));
      return md(c.t('Create user or contact Support to update the email address in Dado'));
    }
  },
  [IssueKind.SENT_ITEM__SLACK_CHANNEL_ACCESS]: {
    category: 'Message delivery: Slack',
    summary: (g) => {
      const d = dataOf(g);
      const supportingActor = ctxOf(g).supportingActors?.[d.actor];
      const channelName =
        supportingActor &&
        supportingActor.type === SupportingActorType.CHANNEL &&
        supportingActor.channeltype === ChannelType.SLACK
          ? supportingActor.channelName
          : undefined;
      return ctxOf(g).t("Couldn't find Slack channel  **{{channel}}** ", {
        channel: `#${channelName}`
      });
    },
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__SLACK_CHANNEL_ACCESS]!.summary(g),
    howToFixIt: (g) =>
      md(ctxOf(g).t('Make sure the channel exists and that the Dado bot is a member of it'))
  },
  [IssueKind.SENT_ITEM__SLACK_DEFAULT]: {
    category: 'Message delivery: Slack',
    summary: (g) => ctxOf(g).t(`Failed to send via Slack`),
    whatCausedIt: (g) => ctxOf(g).t(`Failed to send via Slack`),
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  },

  [IssueKind.SENT_ITEM__TEAMS_USER_NOT_FOUND]: {
    category: 'Message delivery: Teams',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      if (!d.message.recipientEmail)
        return c.t(`Couldn't send via Teams - **{{ actor }}** missing **Work Email**`, {
          actor: actorLabelOf(g)
        });
      return c.t(`Couldn't find Teams user with the email **{{ recipientEmail }}**`, {
        recipientEmail: d.message.recipientEmail
      });
    },
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__TEAMS_USER_NOT_FOUND]!.summary(g),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      if (!d.message.recipientEmail)
        return md(c.t('Add Work Email to import source and run a new import'));
      return md(c.t('Check user has Dado bot installed, or add user to your Teams organization'));
    }
  },
  [IssueKind.SENT_ITEM__TEAMS_OTHER]: {
    category: 'Message delivery: Teams',
    summary: (g) =>
      ctxOf(g).t(`Unexpected error sending via Teams`, {
        recipientEmail: dataOf(g).message.error?.details
      }),
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__TEAMS_OTHER]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  },
  [IssueKind.SENT_ITEM__TEAMS_NO_ERROR]: {
    category: 'Message delivery: Teams',
    // Faithful port: the text cascade has no local default for "Teams,
    // no error object" — it falls through to the function's absolute final
    // fallback ("Unknown error", translated). The JSX cascade's Teams helper
    // returns a literal, untranslated 'Unknown Error' for the same case.
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: () => <LightMdToJsx>Unknown Error</LightMdToJsx>
  },

  [IssueKind.SENT_ITEM__TWILIO_NO_PHONE_NUMBER]: {
    category: 'Message delivery: SMS',
    summary: (g) =>
      ctxOf(g).t(`Couldn't send via SMS - **{{ actor }}** missing phone number`, {
        actor: actorLabelOf(g)
      }),
    whatCausedIt: (g) =>
      sentItemLegacyCopy[IssueKind.SENT_ITEM__TWILIO_NO_PHONE_NUMBER]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Add phone number to import source and run a new import'))
  },
  [IssueKind.SENT_ITEM__TWILIO_UNREACHABLE]: {
    category: 'Message delivery: SMS',
    summary: (g) =>
      ctxOf(g).t(`Couldn't send via SMS - phone number **{{ phoneNumber }}** unreachable`, {
        phoneNumber: dataOf(g).message.to
      }),
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__TWILIO_UNREACHABLE]!.summary(g),
    // No dedicated JSX branch for '30003' — falls to the function's outer default.
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  },
  [IssueKind.SENT_ITEM__TWILIO_INVALID]: {
    category: 'Message delivery: SMS',
    summary: (g) =>
      ctxOf(g).t(
        `Couldn't send via SMS - phone number **{{ phoneNumber }}** is invalid or no longer exists`,
        { phoneNumber: dataOf(g).message.to }
      ),
    whatCausedIt: (g) => sentItemLegacyCopy[IssueKind.SENT_ITEM__TWILIO_INVALID]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Update phone number in import source and run a new import'))
  },
  [IssueKind.SENT_ITEM__TWILIO_OTHER]: {
    category: 'Message delivery: SMS',
    summary: (g) => ctxOf(g).t(`Couldn't send via SMS - other error`),
    whatCausedIt: (g) => ctxOf(g).t(`Couldn't send via SMS - other error`),
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  },

  [IssueKind.SENT_ITEM__UNKNOWN]: {
    category: 'Message delivery',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  }
};
